import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import {
  Box,
  Container,
  Stack,
  Typography,
  TextField,
  Button,
  Dialog,
  Backdrop,
  DialogContent,
  DialogContentText,
  Grid,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const PracticeRegistration = ({ did }) => {
  const token = localStorage.getItem("Thyra_Token");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const practiceId = location.state?.did || 0;

  const defaultValues = {
    id: 0,
    practiceId: 0,
    firstName: "",
    practiceName: "",
    orgBusiness: "",
    billingEmailAddress: "",
    taxId: "",
    website: "",
    middleName: "",
    lastName: "",
    phoneNumber: "",
    inactive: true,
    isBillingProvider: "",
    positionTitle: "",
    address: "",
    emailAddress: "",
    zipCode: "",
    city: "",
    state: "",
    phoneNo: "",
    gender: "",
    typeOfLicense: "",
    deAno: "",
    clinical: "",
    billingSupervisor: "",
    clinicalSupervisor: "",
    type: "",
    fax: "",
    nPI: "",
    licenseNumber: "",
    ssn: "",
    taxonomy: "",
  };

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues,
  });

  const fields = Object.keys(defaultValues);

  const [submitting, setSubmitting] = useState(false);
  const [isDialogOpenResp, setDialogOpenResp] = useState(false);

  useEffect(() => {
    if (practiceId > 0) {
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/Practices/getPracticeById?id=${practiceId}`,
          { headers }
        )
        .then((response) => {
          fields.forEach((field) => setValue(field, response[field]));
          console.log("Fetched data:", response);
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
    }
  }, [practiceId, setValue]);

  const onSubmit = (data) => {
    setSubmitting(true);
    console.log("Submitted data", data);

    if (!data.emailAddress) {
      alert("Please enter a valid email address.");
      setSubmitting(false);
      return;
    }

    axios
      .post(`${process.env.REACT_APP_API_URL}/Practices/AddPractice`, data, {
        headers,
      })
      .then((response) => {
        if (response.id) {
          setSubmitting(false);
          setDialogOpenResp(true);
          setTimeout(() => {
            navigate(`/practice?pid=${response.id}`);
            setDialogOpenResp(false);
          }, 1000);
        } else {
          setSubmitting(false);
        }
      })
      .catch((error) => {
        console.error("Error submitting data:", error);
        setSubmitting(false);
      });
  };

  const handleCancel = () => {
    navigate("/practice");
  };

  // Consistent Typography styling for all labels
  const labelStyles = {
    fontSize: "14px",
    fontWeight: "500",
    color: "#000",
    marginBottom: "8px",
    display: "block",
  };

  // Consistent TextField styling
  const textFieldStyles = {
    "& .MuiInputBase-root": {
      height: "40px",
      fontSize: "14px",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#d1d1d1",
      },
      "&:hover fieldset": {
        borderColor: "#2B7B7E",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#2B7B7E",
      },
    },
  };

  return (
    <Container sx={{ pt: 10, pb: 4, maxWidth: "lg" }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", color: "#2B7B7E" }}>
          Practice Registration
        </Typography>
      </Box>
      <Dialog
        open={isDialogOpenResp}
        onClose={() => setDialogOpenResp(false)}
        BackdropComponent={Backdrop}
        BackdropProps={{ invisible: true }}
        PaperProps={{
          style: {
            marginRight: "5px",
            float: "right",
            marginTop: "5px",
            color: "red",
            cursor: "pointer",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            width: "500px",
            p: 2,
          }}
        >
          <CheckCircleIcon sx={{ fontSize: "100px", color: "green" }} />
          <Typography
            sx={{
              textAlign: "center",
              fontSize: "16px",
              fontWeight: "normal",
              color: "#444444",
              mt: 2,
            }}
          >
            Practice added successfully
          </Typography>
          <DialogContent>
            <DialogContentText></DialogContentText>
          </DialogContent>
        </Box>
      </Dialog>

      {/* Form */}
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          borderRadius: "12px",
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
          p: 4,
        }}
      >
        
        <Grid className="row">
          {/* Practice Name */}
          <Grid className="col-md-4">
            <Typography >Practice Name</Typography>
            <TextField
              {...register("practiceName", {
                required: "Please enter your Practice Name",
              })}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter your Practice Name"
              error={!!errors.practiceName}
              helperText={errors.practiceName?.message}
              sx={textFieldStyles}
            />
          </Grid>

          {/* Org Business */}
           <Grid className="col-md-4">
            <Typography >Org Business</Typography>
            <TextField
              {...register("orgBusiness")}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter your Org Business"
              sx={textFieldStyles}
            />
          </Grid>

          {/* Phone No */}
           <Grid className="col-md-4">
            <Typography >Phone No</Typography>
            <TextField
              {...register("phoneNo")}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter your Phone no"
              onInput={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "").slice(0, 10);
              }}
              sx={textFieldStyles}
            />
          </Grid>

          {/* Fax */}
          <Grid className="col-md-4">
            <Typography sx={labelStyles}>Fax</Typography>
            <TextField
              {...register("fax")}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter your Fax"
              sx={textFieldStyles}
            />
          </Grid>

          {/* Address */}
          <Grid className="col-md-4">
            <Typography sx={labelStyles}>Address</Typography>
            <TextField
              {...register("address")}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter your Address"
              sx={textFieldStyles}
            />
          </Grid>

          {/* City */}
          <Grid className="col-md-4">
            <Typography sx={labelStyles}>City</Typography>
            <TextField
              {...register("city")}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter your City"
              sx={textFieldStyles}
            />
          </Grid>

         

          {/* Zip Code */}
          <Grid className="col-md-4">
            <Typography sx={labelStyles}>Zip Code</Typography>
            <TextField
              {...register("zipCode")}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter your Zip Code"
              onInput={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "").slice(0, 9);
              }}
              sx={textFieldStyles}
            />
          </Grid>

          {/* Email Address */}
          <Grid className="col-md-4">
            <Typography sx={labelStyles}>Email Address</Typography>
            <TextField
              {...register("emailAddress")}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter your Email Address"
              sx={textFieldStyles}
            />
          </Grid>

          {/* Website */}
          <Grid className="col-md-4">
            <Typography sx={labelStyles}>Website</Typography>
            <TextField
              {...register("website")}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter your Website"
              sx={textFieldStyles}
            />
          </Grid>

          {/* NPI */}
          <Grid className="col-md-4">
            <Typography sx={labelStyles}>NPI</Typography>
            <TextField
              {...register("nPI")}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter your NPI"
              sx={textFieldStyles}
            />
          </Grid>

           {/* State */}
           <Grid className="col-md-4">
            <Typography sx={labelStyles}>State</Typography>
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={[
                    { value: "AL", label: "ALABAMA" },
                    { value: "AK", label: "ALASKA" },
                    { value: "AS", label: "AMERICAN SAMOA" },
                    { value: "AZ", label: "ARIZONA" },
                    { value: "AR", label: "ARKANSAS" },
                    { value: "CA", label: "CALIFORNIA" },
                    { value: "CO", label: "COLORADO" },
                    { value: "CT", label: "CONNECTICUT" },
                    { value: "DE", label: "DELAWARE" },
                    { value: "DC", label: "DISTRICT OF COLUMBIA" },
                    { value: "FL", label: "FLORIDA" },
                    { value: "GA", label: "GEORGIA" },
                    { value: "GU", label: "GUAM" },
                    { value: "HI", label: "HAWAII" },
                    { value: "ID", label: "IDAHO" },
                    { value: "IL", label: "ILLINOIS" },
                    { value: "IN", label: "INDIANA" },
                    { value: "IA", label: "IOWA" },
                    { value: "KS", label: "KANSAS" },
                    { value: "KY", label: "KENTUCKY" },
                    { value: "LA", label: "LOUISIANA" },
                    { value: "ME", label: "MAINE" },
                    { value: "MD", label: "MARYLAND" },
                    { value: "MA", label: "MASSACHUSETTS" },
                    { value: "MI", label: "MICHIGAN" },
                    { value: "MN", label: "MINNESOTA" },
                    { value: "MS", label: "MISSISSIPPI" },
                    { value: "MO", label: "MISSOURI" },
                    { value: "MT", label: "MONTANA" },
                    { value: "NE", label: "NEBRASKA" },
                    { value: "NV", label: "NEVADA" },
                    { value: "NH", label: "NEW HAMPSHIRE" },
                    { value: "NJ", label: "NEW JERSEY" },
                    { value: "NM", label: "NEW MEXICO" },
                    { value: "NY", label: "NEW YORK" },
                    { value: "NC", label: "NORTH CAROLINA" },
                    { value: "ND", label: "NORTH DAKOTA" },
                    { value: "MP", label: "NORTHERN MARIANA IS" },
                    { value: "OH", label: "OHIO" },
                    { value: "OK", label: "OKLAHOMA" },
                    { value: "OR", label: "OREGON" },
                    { value: "PA", label: "PENNSYLVANIA" },
                    { value: "PR", label: "PUERTO RICO" },
                    { value: "PI", label: "RHODE ISLAND" },
                    { value: "SC", label: "SOUTH CAROLINA" },
                    { value: "SD", label: "SOUTH DAKOTA" },
                    { value: "TN", label: "TENNESSEE" },
                    { value: "TX", label: "TEXAS" },
                    { value: "UT", label: "UTAH" },
                    { value: "VT", label: "VERMONT" },
                    { value: "VA", label: "VIRGINIA" },
                    { value: "VI", label: "VIRGIN ISLANDS" },
                    { value: "WA", label: "WASHINGTON" },
                    { value: "WV", label: "WEST VIRGINIA" },
                    { value: "WI", label: "WISCONSIN" },
                    { value: "WY", label: "WYOMING" },
                  ]}
                  value={
                    getValues("state")
                      ? {
                          label: getValues("state"),
                          value: getValues("state"),
                        }
                      : null
                  }
                  onChange={(selectedOption) =>
                    setValue("state", selectedOption?.value || "")
                  }
                  styles={{
                    control: (base) => ({
                      ...base,
                      height: "40px",
                      fontSize: "14px",
                      borderColor: "#d1d1d1",
                      "&:hover": {
                        borderColor: "#2B7B7E",
                      },
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 5000,
                    }),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
        
        {/* Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mt: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{
              width: "100px",
              height: "35px",
              backgroundColor: "#2B7B7E",
              color: "white",
              borderRadius: "80px",
              fontSize: "14px",
              "&:hover": { backgroundColor: "#1a1a1a" },
            }}
          >
            <CancelOutlinedIcon
              fontSize="medium"
              style={{ color: "red", paddingRight: "5px" }}
            />
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={submitting}
            sx={{
              width: "100px",
              height: "35px",
              backgroundColor: "#2B7B7E",
              color: "white",
              borderRadius: "80px",
              fontSize: "14px",
              "&:hover": { backgroundColor: "#1a1a1a" },
            }}
          >
            <SaveOutlinedIcon sx={{ color: "white", mr: 1 }} />
            Save
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default PracticeRegistration;